import React, { useState, useEffect } from "react";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import { useAuth } from "../../Common/Configurations/AuthContext";
import DeleteAlert from "../../Common/Layout/DeleteAlert";
import moment from "moment";
import { toast } from 'react-toastify';
import "../Package/Package.css";     // Ensure correct path
import "../Package/PackageForm.css"; // Ensure correct path

const HolidayTransfer = ({ holidayId, packageDetails, onUpdate }) => {
  const initialFieldValues = {
    packageTransferId: "", // Will generate UUID for local identification
    date: "",
    pickupTime: "",
    fromLocation: "",
    toLocation: "",
    vehicleDetails: "",
  };

  const { user } = useAuth();
  const [btnLoading, setBtnLoading] = useState(false);
  const [lists, setLists] = useState([]);
  const [values, setValues] = useState(initialFieldValues);
  const [errors, setErrors] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isFormVisible, setIsFormVisible] = useState(false);

  // 1. Load and Parse Data from Prop (JSON String)
  useEffect(() => {
    if (packageDetails && packageDetails.holidayTransfer) {
        try {
            const parsed = typeof packageDetails.holidayTransfer === 'string'
                ? JSON.parse(packageDetails.holidayTransfer)
                : packageDetails.holidayTransfer;

            // Map keys (Backend PascalCase -> Frontend camelCase)
            const mapped = parsed.map((item, index) => ({
                packageTransferId: item.packageTransferId || item.PackageTransferId || `temp-${index}`,
                date: item.date || item.Date,
                pickupTime: item.pickupTime || item.PickupTime,
                fromLocation: item.fromLocation || item.FromLocation,
                toLocation: item.toLocation || item.ToLocation,
                vehicleDetails: item.vehicleDetails || item.VehicleDetails
            }));
            
            // Sort by date/time
            mapped.sort((a, b) => new Date(`${a.date} ${a.pickupTime}`) - new Date(`${b.date} ${b.pickupTime}`));
            setLists(mapped);
        } catch (e) {
            console.error("JSON Parse Error", e);
            setLists([]);
        }
    } else {
        setLists([]);
    }
  }, [packageDetails]);

  // --- CORE SAVE LOGIC (Updates Holiday JSON) ---
  const saveHolidayData = async (updatedList) => {
      const formData = new FormData();
      formData.append("holidayId", holidayId);
      formData.append("holidayTransfer", JSON.stringify(updatedList)); // Update specific column
      
      if(user?.id) formData.append("userId", user.id);

      try {
          await apiService.update(config.UPDATEHOLIDAY, formData);
          if(onUpdate) {
              onUpdate(); 
          } else {
              setLists(updatedList);
          }
          return true;
      } catch (error) {
          console.error(error);
          toast.error("Failed to update transfers");
          return false;
      }
  };

  // --- HANDLERS ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setBtnLoading(true);

      // 1. ID Management
      const currentId = values.packageTransferId || crypto.randomUUID();
      const newItem = { ...values, packageTransferId: currentId };

      // 2. Update Local Array
      let newList = [...lists];
      const index = newList.findIndex(x => x.packageTransferId === currentId);

      if (index !== -1) {
          newList[index] = newItem; // Edit
      } else {
          newList.push(newItem); // Add
      }

      // 3. Save to Backend
      const success = await saveHolidayData(newList);
      if(success) {
         
          handleCancel();
      }
      setBtnLoading(false);
    }
  };

  const handleDelete = async () => {
    if (itemToDelete !== null) {
      const newList = lists.filter(x => x.packageTransferId !== itemToDelete);
      const success = await saveHolidayData(newList);
      if(success) 
      handleCloseDeleteModal();
    }
  };

  // --- UI Helpers ---
  const handleAddNewClick = () => { resetForm(); setIsFormVisible(true); };
  
  const handleCancel = () => { resetForm(); setIsFormVisible(false); };
  
  const showEditDetails = (data) => {
    const formattedData = {
        ...data,
        date: data.date ? moment(data.date).format('YYYY-MM-DD') : "",
    };
    setValues(formattedData);
    setIsFormVisible(true);
  };

  const resetForm = () => {
    setValues(initialFieldValues);
    setBtnLoading(false);
    setErrors({});
  };

  const validate = () => {
    let temp = {};
    temp.date = values.date !== "";
    temp.pickupTime = values.pickupTime !== "";
    temp.fromLocation = values.fromLocation !== "";
    temp.toLocation = values.toLocation !== "";
    temp.vehicleDetails = values.vehicleDetails !== "";
    setErrors(temp);
    return Object.values(temp).every((x) => x === true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setValues({ ...values, [name]: value });
  };

  const handleOpenDeleteModal = (id) => { setItemToDelete(id); setShowDeleteModal(true); };
  const handleCloseDeleteModal = () => { setShowDeleteModal(false); setItemToDelete(null); };
  const applyErrorClass = (field) => (errors[field] === false ? " form-control-danger" : "");

  return (
    <div className="PackageTransfer-wrapper">
      <div className="card PackageTransfer-card">
        <div className="card-body">
          {/* --- HEADER --- */}
          <div className="PackageTransfer-header">
            <h4 className="header-title">Transfers & Transportation</h4>
            {!isFormVisible && (
              <button
                type="button"
                className="btn btn-primary PackageTransfer-btn-gradient"
                onClick={handleAddNewClick}
              >
                <i className="ri-add-line me-1"></i> Add Transfer
              </button>
            )}
          </div>

          {/* --- ADD/EDIT FORM --- */}
          {isFormVisible && (
            <div className="PackageTransfer-form-container">
              <h5 className="form-title">
                {values.packageTransferId ? "Edit Transfer Details" : "Add Transfer Details"}
              </h5>
              <form onSubmit={handleSubmit} autoComplete="off">
                <div className="row">
                   <div className="col-lg-3 mb-3">
                      <label className="form-label d-flex align-items-center"><i className="ri-road-map-line me-2"></i>From Location</label>
                      <input type="text" name="fromLocation" value={values.fromLocation} onChange={handleInputChange} className={"form-control" + applyErrorClass("fromLocation")} placeholder="e.g. Airport"/>
                      {errors.fromLocation === false && <div className="validationerror">Please enter a valid from location</div>}
                  </div>
                   <div className="col-lg-3 mb-3">
                      <label className="form-label d-flex align-items-center"><i className="ri-map-pin-line me-2"></i>To Location</label>
                      <input type="text" name="toLocation" value={values.toLocation} onChange={handleInputChange} className={"form-control" + applyErrorClass("toLocation")} placeholder="e.g. Hotel"/>
                      {errors.toLocation === false && <div className="validationerror">Please enter a valid to location</div>}
                  </div>
                  <div className="col-lg-2 mb-3">
                       <label className="form-label d-flex align-items-center"><i className="ri-calendar-event-line me-2"></i>Date</label>
                       <input type="date" name="date" value={values.date} onChange={handleInputChange} className={"form-control" + applyErrorClass("date")}/>
                       {errors.date === false && <div className="validationerror">Please select a valid date</div>}
                  </div>
                  <div className="col-lg-2 mb-3">
                       <label className="form-label d-flex align-items-center"><i className="ri-time-line me-2"></i>Pickup Time</label>
                       <input type="time" name="pickupTime" value={values.pickupTime} onChange={handleInputChange} className={"form-control" + applyErrorClass("pickupTime")}/>
                        {errors.pickupTime === false && <div className="validationerror">Please select a valid pickup time</div>}
                  </div>
                  <div className="col-lg-2 mb-3">
                       <label className="form-label d-flex align-items-center"><i className="ri-taxi-line me-2"></i>Vehicle</label>
                       <input type="text" name="vehicleDetails" value={values.vehicleDetails} onChange={handleInputChange} className={"form-control" + applyErrorClass("vehicleDetails")} placeholder="e.g. Sedan"/>
                        {errors.vehicleDetails === false && <div className="validationerror">Please enter valid vehicle details</div>}
                  </div>
                </div>
                <div className="d-flex justify-content-end gap-2 mt-2">
                    <button type="button" className="btn btn-light" onClick={handleCancel}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={btnLoading}>
                        {btnLoading ? "Saving..." : (values.packageTransferId ? "Update" : "Submit")}
                    </button>
                </div>
              </form>
            </div>
          )}

          {/* --- TRANSFER LIST --- */}
          <div className="mt-4">
            {lists.length > 0 ? (
              <div className="PackageTransfer-list">
                {lists.map((v, i) => (
                  <div key={v.packageTransferId || i} className="PackageTransfer-list-item">
                    <div className="item-main-content">
                        <div className="item-icon">
                          <i className="ri-arrow-left-right-line"></i>
                        </div>
                        <div className="item-locations">
                            <span className="location-point">{v.fromLocation}</span>
                            <i className="ri-arrow-right-line location-arrow"></i>
                            <span className="location-point">{v.toLocation}</span>
                        </div>
                    </div>
                    <div className="item-details">
                        <div className="detail-item">
                            <i className="ri-calendar-2-line"></i> {moment(v.date).format("ddd, DD MMM YYYY")}
                        </div>
                         <div className="detail-item">
                            <i className="ri-time-line"></i> {v.pickupTime}
                        </div>
                         <div className="detail-item">
                            <i className="ri-taxi-wifi-line"></i> {v.vehicleDetails}
                        </div>
                    </div>
                    <div className="item-actions">
                        <button className="PackageTransfer-action-btn edit" onClick={() => showEditDetails(v)}><i className="ri-pencil-fill"/></button>
                        <button className="PackageTransfer-action-btn delete" onClick={() => handleOpenDeleteModal(v.packageTransferId)}><i className="ri-delete-bin-fill"/></button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
                <div className="PackageTransfer-empty-state">
                    <i className="ri-roadster-line empty-icon"></i>
                    <h5>No Transfers Added</h5>
                    <p>Click "Add Transfer" to include transportation details.</p>
                </div>
            )}
          </div>
        </div>
      </div>
      <DeleteAlert show={showDeleteModal} handleClose={handleCloseDeleteModal} handleDelete={handleDelete} />
    </div>
  );
};

export default HolidayTransfer;